import { Box, Menu, MenuItem, TableCell, TableRow, Typography, Divider } from '@mui/material'
import React, { memo, useCallback, useState, useRef, useEffect } from 'react'
import TableCellWrapper from './TableCellWrapper'
import { getColumnWidth } from '../../../../../../src/utility/columnWidth.js'
import {
  getCommentId,
  getConstraintErrorIdForColumn,
  getLegacySubfieldId,
  getReviewId,
  getTableCommentId,
  getTableReviewId,
} from '../../../utils/utils'
import {
  useFieldValidationError,
  usePreviousAllReviewData,
  usePreviousProcessData,
  useProcessContext,
} from '../../../../../store'
import Checkbox from '@mui/material/Checkbox'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { ACTIVITY_TYPES, TABLE_ADD_ROW_TYPE } from '../../../../../utility/constants'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ControlPointDuplicateIcon from '@mui/icons-material/ControlPointDuplicate'

const label = { inputProps: { 'aria-label': 'Checkbox demo' } }

const TableRowWrapper = ({
  index,
  row,
  allRowsWithHeaders = [],
  columns,
  style = {},
  columnWidth = '100%',
  currentRowData,
  handleRowDataChange = () => {},
  currentCommentDataRow,
  currentReviewDataRow,
  isSelected,
  isSelectable,
  isImported,
  allowRowDelete,
  allowAddRows,
  allowTableHeaders,
  isFieldDisabled = () => false,
  isHidden = () => false,
  handleRowSelect = () => {},
  handleRowDelete = () => {},
  syncWithParent = () => {},
  addNewRowInBetween = () => {},
  addHeader = () => {},
  optionsDisabled,
  freezeColumnId,
  hiddenColumnIds = [],
}) => {
  const [rowOptionsAnchorEl, setRowOptionsAnchorEl] = useState(null)
  const heading1ref = useRef(null)
  const [actionWidth, setActionWidth] = useState(0)

  const serialNoRef = useRef(null)
  const [serialNoWidth, setSerialNoWidth] = useState(0)

  useEffect(() => {
    if (heading1ref.current) {
      setActionWidth(heading1ref.current.offsetWidth)
    }
    if (serialNoRef.current) {
      setSerialNoWidth(serialNoRef.current.offsetWidth)
    }
  }, [])

  const handleRowOptionsClick = (event) => {
    setRowOptionsAnchorEl(event.currentTarget)
  }

  const handleRowOptionsClose = () => {
    setRowOptionsAnchorEl(null)
  }

  /* field validation error */
  const fieldValidationError = useFieldValidationError((state) => state.fieldValidationError)
  const processContext = useProcessContext((state) => state.processContext)
  const activityType = processContext?.activityTemplate?.activity_type
  const version = processContext?.processData?.version

  /*Previous Process Data*/
  const previousReviewProcessDataValues = usePreviousProcessData((state) =>
    state.previousProcessData.prevReviewProcessData
      ? state.previousProcessData.prevReviewProcessData
      : {}
  )

  /*Previous All Reviews */
  const previousAllReviews = usePreviousAllReviewData((state) => state.previousAllReviews)

  const previousReviewDataOfCurrentRow = columns?.reduce((acc, column) => {
    if (Object.keys(previousReviewProcessDataValues).length > 0) {
      if (!column.is_read_only || !column.is_computed) {
        return {
          ...acc,
          [getReviewId(column.id)]:
            previousReviewProcessDataValues.review_data[getTableReviewId(column.id, row)],
        }
      } else {
        return { ...acc }
      }
    }
  }, {})

  const previousReviewCommentsDataOfCurrentRow = columns?.reduce((acc, column) => {
    if (Object.keys(previousReviewProcessDataValues).length > 0) {
      if (!column.is_read_only || !column.is_computed) {
        return {
          ...acc,
          [getCommentId(column.id)]:
            previousReviewProcessDataValues.review_comments_data[getTableCommentId(column.id, row)],
        }
      } else {
        return { ...acc }
      }
    }
  }, {})

  const previousAllReviewsOfCurrentRow = columns?.reduce((acc, column) => {
    if (Object.keys(previousAllReviews).length > 0) {
      if (!column.is_read_only || !column.is_computed) {
        return {
          ...acc,
          [getReviewId(column.id)]: previousAllReviews[getTableReviewId(column.id, row)],
        }
      } else {
        return { ...acc }
      }
    }
  }, {})

  const onChangeHandler = useCallback(
    async (id, v, dataType) => {
      handleRowDataChange(id, v, dataType)
    },
    [currentRowData, currentCommentDataRow, currentReviewDataRow]
  )

  const isReviewSwitchHidden = (field) => {
    return (activityType == ACTIVITY_TYPES.DATA_ENTRY && version == 1) || field.is_read_only
  }

  const renderSerialNo = (index) => {
    return (
      <TableCell
        ref={serialNoRef}
        sx={{
          minWidth: 60,
          position: 'sticky',
          backgroundColor: 'white',
          left: actionWidth,
        }}
      >
        {index + 1}
      </TableCell>
    )
  }

  const renderActions = (row) => {
    return (
      <TableCell
        ref={heading1ref}
        sx={{
          position: 'sticky',
          left: 0,
          backgroundColor: 'white',
          minWidth: isSelectable ? 84 : 46,
          top: style.top,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <MoreVertIcon onClick={handleRowOptionsClick} />
          <Menu
            anchorEl={rowOptionsAnchorEl}
            open={Boolean(rowOptionsAnchorEl)}
            onClose={handleRowOptionsClose}
          >
            <MenuItem
              sx={{ textTransform: 'capitalize' }}
              style={{ columnGap: '5px' }}
              disabled={!allowAddRows}
              onClick={() => {
                addNewRowInBetween(row, TABLE_ADD_ROW_TYPE.ADD_ROW)
                handleRowOptionsClose()
              }}
            >
              <AddCircleOutlineIcon fontSize="small" />
              Add Row
            </MenuItem>
            <MenuItem
              style={{ columnGap: '5px' }}
              disabled={!allowAddRows}
              onClick={() => {
                addNewRowInBetween(row, TABLE_ADD_ROW_TYPE.COPY_ROW)
                handleRowOptionsClose()
              }}
            >
              <ControlPointDuplicateIcon fontSize="small" />
              Copy Row
            </MenuItem>
            <MenuItem
              style={{ columnGap: '5px' }}
              disabled={optionsDisabled || !allowTableHeaders}
              onClick={() => {
                addHeader(row, allRowsWithHeaders)
                handleRowOptionsClose()
              }}
            >
              <TextFieldsIcon fontSize="small" />
              Add Header
            </MenuItem>
            <Divider style={{ marginBlock: '4px' }} />
            <MenuItem
              sx={{ color: 'red', columnGap: '5px' }}
              disabled={
                !(allRowsWithHeaders.filter((it) => it.row != null).length > 1) || !allowRowDelete
              }
              onClick={() => {
                if (
                  (activityType == ACTIVITY_TYPES.DATA_ENTRY ||
                    activityType === ACTIVITY_TYPES.LONG_RUNNING) &&
                  allowRowDelete
                ) {
                  handleRowDelete(row)
                  handleRowOptionsClose()
                }
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
              Delete
            </MenuItem>
          </Menu>
          {isSelectable ? (
            <>
              {' '}
              <Checkbox
                {...label}
                checked={isSelected}
                color="primary"
                size="small"
                disabled={activityType != ACTIVITY_TYPES.DATA_ENTRY}
                onClick={(e) => handleRowSelect(row, index, currentRowData)}
              />
            </>
          ) : null}
        </Box>
      </TableCell>
    )
  }

  const calculateOffset = (index) => {
    const staticPartWidth = actionWidth + serialNoWidth

    // Filter out hidden columns
    const visibleColumns = columns.filter((fld) => !hiddenColumnIds?.includes(fld.id))

    if (index <= 0) {
      return staticPartWidth
    } else {
      // Calculate offset based only on visible columns
      const dynamicPartWidth =
        staticPartWidth +
        visibleColumns
          .slice(0, index)
          .reduce((acc, curr) => acc + getColumnWidth(curr, isReviewSwitchHidden), 0)
      return dynamicPartWidth
    }
  }

  return (
    <>
      <TableRow id={`tr-${row}`} style={style}>
        {currentRowData &&
          (activityType == ACTIVITY_TYPES.DATA_ENTRY ||
            activityType === ACTIVITY_TYPES.LONG_RUNNING) &&
          renderActions(row)}
        {currentRowData && renderSerialNo(index)}
        {currentRowData &&
          columns
            ?.filter((it) => !hiddenColumnIds?.includes(it.id)) // Filter out hidden columns
            ?.map((it, index) => {
              const visibleColumns = columns.filter((col) => !hiddenColumnIds?.includes(col.id))

              const freezeIndex = visibleColumns.findIndex((col) =>
                freezeColumnId?.includes(col.id)
              )

              const isFrozen = visibleColumns.indexOf(it) <= freezeIndex

              // Calculate the correct leftOffset based on visible columns
              const leftOffset = calculateOffset(visibleColumns.indexOf(it))

              return (
                !isHidden(it, row) && (
                  <TableCellWrapper
                    isReviewSwitchHidden={isReviewSwitchHidden}
                    isFrozen={isFrozen}
                    leftOffset={leftOffset}
                    key={getLegacySubfieldId(it.id, row)}
                    field={it}
                    rowId={row}
                    isSelectable={isSelectable}
                    isSelected={isSelected}
                    isImported={isImported}
                    rowData={currentRowData}
                    rowReview={currentReviewDataRow}
                    rowComment={currentCommentDataRow}
                    previousReviewDataOfCurrentRow={previousReviewDataOfCurrentRow}
                    previousReviewCommentsDataOfCurrentRow={previousReviewCommentsDataOfCurrentRow}
                    previousAllReviewsOfCurrentRow={previousAllReviewsOfCurrentRow}
                    onChangeValue={onChangeHandler}
                    columnWidth={columnWidth}
                    helperText={fieldValidationError[getConstraintErrorIdForColumn(it.id, row)]}
                    isFieldDisabled={isFieldDisabled}
                    syncWithParent={syncWithParent}
                  />
                )
              )
            })}
      </TableRow>
    </>
  )
}

export default memo(TableRowWrapper, (prevProps, nextProps) => {
  return (
    prevProps?.currentRowData?.updatedAt === nextProps?.currentRowData?.updatedAt &&
    prevProps?.currentReviewDataRow?.updatedAt === nextProps?.currentReviewDataRow?.updatedAt &&
    prevProps?.currentCommentDataRow?.updatedAt === nextProps?.currentCommentDataRow?.updatedAt &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.normalFieldLastUpdateAt === nextProps.normalFieldLastUpdateAt &&
    prevProps.reviewLastUpdatedAt === nextProps.reviewLastUpdatedAt &&
    prevProps.commentLastUpdatedAt === nextProps.commentLastUpdatedAt &&
    prevProps.freezeColumnId === nextProps.freezeColumnId
  )
})
